import type {CalendarDisplayMetricColumnRef} from "~/domainModels/calendarDisplayMetrics/types";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

type CalendarMetricRow = {
    createdAt?: string;
    updatedAt?: string;
    additionalColumnValues?: Record<Uuid, unknown>;
    journey?: {
        createdAt?: string;
        updatedAt?: string;
        additionalColumnValues?: Record<Uuid, unknown>;
    };
    eventContainer?: {
        createdAt?: string;
        updatedAt?: string;
        additionalColumnValues?: Record<Uuid, unknown>;
    };
};

function isValidCalendarIso(value: string): boolean {
    const trimmed = value.trim();
    return trimmed !== "" && Number.isNaN(Date.parse(trimmed)) === false;
}

function toScalarString(value: unknown): string | undefined {
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return undefined;
}

function readAdditionalColumnValue(row: CalendarMetricRow, columnId: Uuid): unknown {
    const fromRow = row.additionalColumnValues?.[columnId];
    const fromRowString = toScalarString(fromRow);
    if (fromRowString !== undefined && fromRowString.trim() !== "") {
        return fromRow;
    }
    const fromJourney =
        row.journey?.additionalColumnValues?.[columnId] ??
        row.eventContainer?.additionalColumnValues?.[columnId];
    const fromJourneyString = toScalarString(fromJourney);
    if (fromJourneyString !== undefined && fromJourneyString.trim() !== "") {
        return fromJourney;
    }
    return undefined;
}

/** Resolves calendar tile placement ISO datetime; returns undefined when the metric value is absent or invalid. */
export function resolveCalendarMetricIsoStart(
    row: unknown,
    columnRef: CalendarDisplayMetricColumnRef,
): string | undefined {
    if (columnRef.kind === "custom") {
        return columnRef.resolveIsoStart(row);
    }

    const metricRow = row as CalendarMetricRow;

    if (columnRef.kind === "system") {
        const raw =
            columnRef.columnId === "createdAt"
                ? metricRow.createdAt ?? metricRow.journey?.createdAt ?? metricRow.eventContainer?.createdAt
                : metricRow.updatedAt ?? metricRow.journey?.updatedAt ?? metricRow.eventContainer?.updatedAt;
        if (raw === undefined || isValidCalendarIso(String(raw)) === false) {
            return undefined;
        }
        return String(raw);
    }

    const raw = readAdditionalColumnValue(metricRow, columnRef.columnId);
    const rawString = toScalarString(raw);
    if (rawString === undefined || isValidCalendarIso(rawString) === false) {
        return undefined;
    }
    return rawString;
}
